import {
  Type,
  Input,
  inject,
  Component,
  DestroyRef,
  ViewChild,
  ViewContainerRef,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { CdkDragMove } from '@angular/cdk/drag-drop';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

import { LoggerService } from '@services/logger.service';
import { SidebarService } from '../../services/sidebar.service';
import { PanelComponentMappingService } from '../../services/panel-component-mapping.service';

import { ToolbarButton } from '../../models/toolbar.models';
import { SidebarButtonConfig } from '../sidebar/sidebar.component';
import { tap } from 'rxjs';

@Component({
  selector: 'app-sidebar-panel',
  templateUrl: './sidebar-panel.component.html',
  styleUrls: ['./sidebar-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SidebarPanelComponent implements OnInit {
  @ViewChild('panelContainer', { read: ViewContainerRef })
  container!: ViewContainerRef;

  @Input() isOpen: boolean = false;
  @Input() title: string = '';
  @Input() icon: IconProp;
  buttons: ToolbarButton[] = [];
  showBackButton: boolean = false;
  wideSidebar: boolean = false;
  sidebarComponentInstance: any;
  hideButtons: boolean = false;

  protected destroyRef = inject(DestroyRef);
  protected logger = inject(LoggerService);

  constructor(
    private componentMappingService: PanelComponentMappingService,
    private sidebarPanelService: PanelComponentMappingService,
    private sidebarService: SidebarService,
  ) {}

  ngOnInit() {
    this.sidebarPanelService.setChildInstance(this);

    this.sidebarService.hideButtons$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((hideButtons) => {
        this.hideButtons = hideButtons;
      });
    // * Sidebar close events
    this.sidebarService.isOpen$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((isOpen) => {
          if (!isOpen) {
            this.destroyCurrentComponent();
          }
        }),
      )
      .subscribe();
  }

  private destroyCurrentComponent() {
    if (this.container) {
      this.container.clear();
      this.sidebarComponentInstance = null;
      this.buttons = [];
      this.showBackButton = false;
    }
  }

  onDragMoved(event: CdkDragMove): void {
    if (event.pointerPosition) {
      // Set the position of the dragged element during the drag operation
      this.container.element.nativeElement.style.left = `${event.pointerPosition.x}px`;
      this.container.element.nativeElement.style.top = `${event.pointerPosition.y}px`;
    }
  }

  loadComponent(sidebarButton: SidebarButtonConfig): void {
    const componentType: Type<any> | null =
      this.componentMappingService.getComponentType(
        sidebarButton.componentName,
      );
    if (componentType) {
      // * destroy any existing Component
      this.destroyCurrentComponent();

      const componentRef = this.container.createComponent(componentType);
      const instance = componentRef.instance;

      if (sidebarButton.inputs) {
        Object.keys(sidebarButton.inputs).forEach((key) => {
          instance[key] = sidebarButton.inputs[key];
        });
      }

      if (instance.buttons$) {
        instance.buttons$
          .pipe(
            tap((updatedButtons: ToolbarButton[]) => {
              this.buttons = updatedButtons;
            }),
            takeUntilDestroyed(this.destroyRef),
          )
          .subscribe();
      }

      if (instance.title$) {
        instance.title$
          .pipe(
            tap((updatedTitle: string) => {
              this.title = updatedTitle;
            }),
            takeUntilDestroyed(this.destroyRef),
          )
          .subscribe();
      }

      if (instance.showBackButton$) {
        instance.showBackButton$
          .pipe(
            tap((showBackButton: boolean) => {
              this.showBackButton = showBackButton;
            }),
            takeUntilDestroyed(this.destroyRef),
          )
          .subscribe();
      } else {
        this.showBackButton = false;
      }

      if (instance.wideSidebar) {
        this.wideSidebar = instance.wideSidebar;
        this.sidebarService.setSidebarWidth(true);
      } else {
        this.wideSidebar = false;
        this.sidebarService.setSidebarWidth(false);
      }

      this.sidebarComponentInstance = instance;
    }
  }

  back() {
    if (
      this.sidebarComponentInstance &&
      this.sidebarComponentInstance.sidebarBack
    ) {
      this.sidebarComponentInstance.sidebarBack();
    } else {
      this.logger.error(
        'No sidebarComponentInstance or sidebarBack method found',
        this.sidebarComponentInstance,
      );
    }
  }

  closeSidebar(): void {
    this.sidebarService.closeSidebar();
  }
}
